<template>
  <BaseShowEditDateRangePicker
    :start="isoBounds[0]"
    :end="isoBounds[1]"
    :label="label"
    :name="name"
    :edition-mode="true"
    :errors="errors"
    @update:start="onStart"
    @update:end="onEnd"
  />
</template>

<script>
import moment from 'moment'
import BaseShowEditDateRangePicker from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateRangePicker.vue'

/**
 * Date range input replacing litepie-datepicker, drawn by Funeral Manager's
 * BaseShowEditDateRangePicker. Keeps litepie's `v-model` contract: the
 * emitted shape follows the bound value's — a `'from - to'` string for a
 * string, `[from, to]` for an array, `{ startDate, endDate }` otherwise —
 * each date formatted with `formatter.date`.
 */
export default {
  name: 'BaseDateRangeInput',
  components: { BaseShowEditDateRangePicker },
  props: {
    modelValue: {
      type: [String, Array, Object],
      required: false,
      default: null
    },
    formatter: {
      type: Object,
      required: false,
      default: () => ({ date: 'DD/MM/YYYY' })
    },
    separator: { type: String, required: false, default: ' - ' },
    label: { type: String, required: false, default: null },
    name: { type: String, required: false, default: 'period' },
    errors: { type: [Array, Object], required: false, default: null }
  },
  emits: ['update:modelValue'],
  data() {
    return { pendingStart: null }
  },
  computed: {
    format() {
      return this.formatter?.date ?? 'DD/MM/YYYY'
    },
    bounds() {
      const value = this.modelValue
      if (!value) return [null, null]
      if (typeof value === 'string') {
        const [start, end] = value.split(this.separator)
        return [start || null, end || null]
      }
      if (Array.isArray(value)) return [value[0] ?? null, value[1] ?? null]
      return [value.startDate || null, value.endDate || null]
    },
    isoBounds() {
      return this.bounds.map((date) => {
        const parsed = date ? moment(date, this.format) : null
        return parsed?.isValid() ? parsed.format('YYYY-MM-DD') : null
      })
    }
  },
  methods: {
    // The picker emits `update:start` then `update:end` for one selection.
    onStart(iso) {
      this.pendingStart = iso
      if (!iso) this.emitRange(null, null)
    },
    onEnd(iso) {
      const start = this.pendingStart ?? this.isoBounds[0]
      this.pendingStart = null
      if (!start || !iso) {
        this.emitRange(null, null)
        return
      }
      this.emitRange(
        moment(start, 'YYYY-MM-DD').format(this.format),
        moment(iso, 'YYYY-MM-DD').format(this.format)
      )
    },
    emitRange(start, end) {
      const value = this.modelValue
      if (typeof value === 'string') {
        this.$emit(
          'update:modelValue',
          start ? `${start}${this.separator}${end}` : ''
        )
      } else if (Array.isArray(value)) {
        this.$emit('update:modelValue', start ? [start, end] : [])
      } else {
        this.$emit('update:modelValue', {
          startDate: start ?? '',
          endDate: end ?? ''
        })
      }
    }
  }
}
</script>
